import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchField } from "@/components/ui/search-field";
import { typeMetaClass } from "@/lib/typography";
import { cn } from "@/lib/utils";

export interface SearchPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The surface's own glyph — a compass for topics, a waveform for speech. */
  icon: LucideIcon;
  title: string;
  description: string;
  placeholder: string;
  /** `wide` is for the pronunciation search, which is a player plus a rail. */
  width?: "md" | "wide";
  term: string;
  onTermChange: (value: string) => void;
  /** Enter in the field — always the live value, never the debounced one. */
  onSubmit: () => void;
  footerLabel: string;
  footerDisabled?: boolean;
  onFooterClick: () => void;
  bodyClassName?: string;
  children: ReactNode;
}

/**
 * The chrome both search dialogs share.
 *
 * It is a study desk, not a command palette: a tinted glyph, the surface's name
 * set in Fraunces, one line saying what it does, and the field directly under
 * it. The name is *visible* on purpose — a learner opening "Pronunciation"
 * should be told what they are about to hear, and the two dialogs should never
 * be mistaken for one another.
 *
 * Everything that must not differ between the two (where the dialog sits, how
 * Enter submits, the footer's job) lives here; the body is the caller's — a list
 * of topics in one, a player and a clip rail in the other.
 */
export function SearchPalette({
  open,
  onOpenChange,
  icon: Icon,
  title,
  description,
  placeholder,
  width = "md",
  term,
  onTermChange,
  onSubmit,
  footerLabel,
  footerDisabled = false,
  onFooterClick,
  bodyClassName,
  children,
}: SearchPaletteProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="xl"
        /**
         * Radix would focus the first tabbable element — the footer action — so
         * the dialog would open with the caret on a button. A search palette's
         * whole purpose is typing, so the field takes focus.
         */
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const field = (event.target as HTMLElement | null)?.querySelector("input");
          field?.focus();
        }}
        className={cn(
          // A palette hangs near the top of the window rather than sitting in
          // the middle of it: the eye starts at the field, not at the centre.
          "flex translate-y-0 flex-col gap-0 overflow-hidden rounded-2xl p-0",
          // The wide one carries a player, so it takes more of the window and
          // starts higher — otherwise the desk's caption line and its "open the
          // full lesson" action sit under the footer.
          width === "wide"
            ? "top-[3vh] max-h-[94vh] sm:max-w-4xl"
            : "top-[10vh] max-h-[86vh] sm:max-w-2xl",
          // The footer action and Escape both close it; a third X beside the
          // field is one control too many. Only the absolutely positioned
          // built-in close button is matched — the footer action is a direct
          // child too, and hiding every direct-child button hid it.
          "[&>button.absolute]:hidden",
        )}
      >
        <div className="flex items-start gap-3 px-4 pb-1.5 pt-3">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-text ring-1 ring-inset ring-primary/15 dark:bg-primary/15 dark:text-primary"
            aria-hidden
          >
            <Icon className="h-[18px] w-[18px]" />
          </span>

          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display text-[17px] font-semibold leading-tight tracking-tight text-stone-900 dark:text-stone-50">
              {title}
            </DialogTitle>
            <DialogDescription className="mt-0.5 truncate text-[11px] leading-snug text-stone-500 dark:text-stone-400">
              {description}
            </DialogDescription>
          </div>

          <kbd className="mt-0.5 hidden shrink-0 rounded-md border border-stone-200 bg-stone-50 px-1.5 py-0.5 font-mono text-[11px] text-stone-500 sm:inline-flex dark:border-stone-700 dark:bg-stone-800 dark:text-stone-400">
            Esc
          </kbd>
        </div>

        <div className="px-4 pb-3 pt-1">
          <SearchField
            value={term}
            onChange={onTermChange}
            placeholder={placeholder}
            ariaLabel={title}
            dataTour="global-search"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onSubmit();
              }
            }}
          />
        </div>

        <div
          className={cn(
            "min-h-[168px] flex-1 border-t border-stone-200/70 dark:border-stone-800",
            bodyClassName ?? "overflow-y-auto",
          )}
        >
          {children}
        </div>

        {/* One action, always in the same place: the field finds a few, this
            hands the query to the surface that can page through all of it. */}
        <button
          type="button"
          onClick={onFooterClick}
          disabled={footerDisabled}
          className={cn(
            "flex w-full items-center justify-between gap-3 border-t border-stone-200/70 px-4 py-2.5 text-left text-xs transition-colors dark:border-stone-800",
            footerDisabled
              ? "cursor-default text-muted-foreground"
              : "text-foreground hover:bg-stone-50 dark:hover:bg-stone-800/50",
          )}
        >
          <span className="truncate font-medium">{footerLabel}</span>
          <kbd
            className={cn(
              "shrink-0 rounded border px-1.5 py-0.5 font-mono text-[11px]",
              footerDisabled
                ? "border-stone-200/60 text-stone-400 dark:border-stone-800"
                : "border-stone-200 bg-stone-50 text-stone-500 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-400",
            )}
          >
            ↵
          </kbd>
        </button>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A quiet state that still teaches.
 *
 * A glyph, one sentence saying what this surface answers, and a second line
 * saying how to ask — an empty search is the moment someone needs to be told
 * how the search works, not a blank rectangle.
 */
export function PaletteHint({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-9 text-center">
      {Icon && (
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400">
          <Icon className="h-[18px] w-[18px]" aria-hidden />
        </span>
      )}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && (
        <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">{description}</p>
      )}
      {children}
    </div>
  );
}

/**
 * "Try one of these" — real English words, because the content being searched is
 * English whatever the interface language is. The label is the translated part.
 */
export function SuggestionChips({
  label,
  words,
  onPick,
}: {
  label: string;
  words: string[];
  onPick: (word: string) => void;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
      <span className={cn(typeMetaClass, "mr-0.5")}>{label}</span>
      {words.map((word) => (
        <button
          key={word}
          type="button"
          onClick={() => onPick(word)}
          className="rounded-full border border-stone-200 bg-white px-2.5 py-1 text-xs font-medium text-stone-600 transition-colors hover:border-primary/40 hover:text-primary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:border-stone-700 dark:bg-stone-800/60 dark:text-stone-300 dark:hover:border-primary/40"
        >
          {word}
        </button>
      ))}
    </div>
  );
}

/** Three quiet bars — shaped like a result row, so nothing shifts on arrival. */
export function ResultSkeleton() {
  return (
    <div className="space-y-3 px-4 py-3" aria-hidden>
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex items-start gap-3">
          <div className="h-8 w-8 shrink-0 animate-pulse rounded-xl bg-stone-200/70 dark:bg-stone-700/50" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div
              className="h-3.5 animate-pulse rounded bg-stone-200/70 dark:bg-stone-700/50"
              style={{ width: `${70 - row * 12}%` }}
            />
            <div className="h-2.5 w-24 animate-pulse rounded bg-stone-200/50 dark:bg-stone-700/40" />
          </div>
        </div>
      ))}
    </div>
  );
}
